/**
 * Voice Vision Service - Camera/Screen Capture for Voice Mode
 * 
 * Allows the AI to "see" through the user's camera or screen during voice conversations.
 * Captures frames at 1fps and sends them to the backend for Gemini Vision analysis.
 * 
 * Features:
 * - Camera capture (back camera by default on mobile for showing things to AI)
 * - Camera flip to switch between front/back cameras
 * - Screen sharing (web only)
 * - Frame capture at configurable FPS (default 1fps)
 * - Automatic cleanup when voice mode stops
 * - Credit tracking (0.5 credits per analyzed frame)
 */

import { Platform } from 'react-native';
import voiceSocketManager from './voiceSocket';
import { voiceApi } from './api';
import { useVoiceModeStore } from '@/store';
import { devLog, devWarn } from '@/lib/devLog';

// Configuration
const DEFAULT_FRAME_INTERVAL_MS = 1000; // 1 frame per second
const MAX_FRAME_WIDTH = 640; // Max width for captured frames (for bandwidth)
const JPEG_QUALITY = 0.7; // JPEG compression quality

/**
 * Voice Vision Service
 * Manages video capture and frame analysis for voice mode
 */
class VoiceVisionService {
  constructor() {
    // Stream and capture state
    this.mediaStream = null;
    this.videoElement = null;
    this.canvasElement = null;
    this.canvasContext = null;
    
    // Capture state
    this.isCapturing = false;
    this.captureInterval = null;
    this.frameIntervalMs = DEFAULT_FRAME_INTERVAL_MS;
    
    // Session state
    this.sessionId = null; // Voice session ID
    this.visionSessionId = null;
    this.source = null; // 'camera' | 'screen'
    
    // Camera state
    this.facingMode = 'environment'; // 'environment' (back) or 'user' (front) - default to back camera
    
    // Stats
    this.framesSent = 0;
    this.lastFrameTime = null;
    
    // Backpressure handling - track pending frames to avoid queue buildup
    this.pendingFrames = 0;
    this.maxPendingFrames = 3; // Skip captures if more than 3 frames are pending
    this.droppedFrames = 0;

    // Transport: legacy Socket.IO/raw-WS sessions stream frames over the
    // socket; OpenAI Realtime WebRTC sessions have no socket, so frames go
    // over REST and the resulting context is injected via the data channel.
    this.useRestTransport = false;

    // Callbacks for state updates
    this.onCameraFlip = null;
  }

  /**
   * Start the vision session on the backend, picking the transport that
   * matches the active voice connection.
   * @param {string} source - 'camera' or 'screen'
   */
  async _startVisionSession(source) {
    if (voiceSocketManager.isRealtimeTransport()) {
      this.useRestTransport = true;
      const resp = await voiceApi.startVision(this.sessionId, source);
      // Honor the backend frame rate limit (e.g. 10 frames/min) instead of
      // posting 1fps frames that would mostly come back rate-limited.
      const maxFrameRate = resp?.config?.maxFrameRate;
      if (maxFrameRate > 0) {
        this.frameIntervalMs = Math.max(DEFAULT_FRAME_INTERVAL_MS, Math.ceil(1000 / maxFrameRate));
      }
      useVoiceModeStore.getState().actions.startVision(source, null);
    } else {
      this.useRestTransport = false;
      voiceSocketManager.startVision(source);
      // Store will be updated via vision_started event handler in voiceSocket
    }
  }

  /**
   * Start camera capture
   * @param {string} voiceSessionId - The current voice session ID
   * @param {string} facingMode - 'environment' (back) or 'user' (front), defaults to 'environment'
   * @returns {Promise<MediaStream>} The camera media stream
   */
  async startCamera(voiceSessionId, facingMode = 'environment') {
    // Vision capture is web-only for now - native would need expo-camera integration
    if (Platform.OS !== 'web') {
      throw new Error('Camera vision is not yet supported on mobile. Coming soon!');
    }
    
    if (this.isCapturing) {
      devLog('[Vision] Already capturing, stopping first');
      await this.stop();
    }

    this.sessionId = voiceSessionId;
    this.source = 'camera';
    this.facingMode = facingMode;

    try {
      // Request camera access - use 'environment' (back camera) by default
      // This is better for showing things to the AI
      devLog('[Vision] Requesting camera with facingMode:', this.facingMode);
      
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: this.facingMode },
          width: { ideal: MAX_FRAME_WIDTH },
          height: { ideal: MAX_FRAME_WIDTH * 0.75 }, // 4:3 aspect ratio
        },
        audio: false, // We only need video
      });

      // Set up video element for frame capture and WAIT for it to be ready
      await this.setupVideoElement();
      
      // Additional wait for video to start playing
      await this.waitForVideoReady();

      // Start vision session on the backend (REST for realtime, socket otherwise)
      await this._startVisionSession('camera');

      // Start frame capture loop
      this.startFrameCapture();

      devLog('[Vision] Camera started successfully, facingMode:', this.facingMode);
      return this.mediaStream;
    } catch (error) {
      console.error('[Vision] Failed to start camera:', error);
      await this.cleanup();
      throw error;
    }
  }

  /**
   * Flip camera between front and back
   * @returns {Promise<MediaStream>} The new camera stream
   */
  async flipCamera() {
    if (Platform.OS !== 'web') {
      devWarn('[Vision] Camera flip not supported on native');
      return null;
    }
    
    if (!this.isCapturing || this.source !== 'camera') {
      devWarn('[Vision] Cannot flip camera - not in camera mode');
      return null;
    }

    const sessionId = this.sessionId;
    const newFacingMode = this.facingMode === 'environment' ? 'user' : 'environment';
    
    devLog('[Vision] Flipping camera from', this.facingMode, 'to', newFacingMode);

    // Stop current capture but don't end the vision session
    this.isCapturing = false;
    if (this.captureInterval) {
      clearInterval(this.captureInterval);
      this.captureInterval = null;
    }

    // Stop current media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }

    // Clean up video element
    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    try {
      // Request new camera with opposite facing mode
      this.facingMode = newFacingMode;
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: this.facingMode },
          width: { ideal: MAX_FRAME_WIDTH },
          height: { ideal: MAX_FRAME_WIDTH * 0.75 },
        },
        audio: false,
      });

      // Set up video element again
      await this.setupVideoElement();
      await this.waitForVideoReady();

      // Restart frame capture
      this.startFrameCapture();

      // Notify callback if set
      if (this.onCameraFlip) {
        this.onCameraFlip(this.facingMode, this.mediaStream);
      }

      devLog('[Vision] Camera flipped successfully to:', this.facingMode);
      return this.mediaStream;
    } catch (error) {
      console.error('[Vision] Failed to flip camera:', error);
      // Try to restore original camera
      try {
        this.facingMode = this.facingMode === 'environment' ? 'user' : 'environment';
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: this.facingMode },
            width: { ideal: MAX_FRAME_WIDTH },
            height: { ideal: MAX_FRAME_WIDTH * 0.75 },
          },
          audio: false,
        });
        await this.setupVideoElement();
        await this.waitForVideoReady();
        this.startFrameCapture();
      } catch (restoreError) {
        console.error('[Vision] Failed to restore camera:', restoreError);
        await this.stop();
      }
      throw error;
    }
  }

  /**
   * Get current facing mode
   */
  getFacingMode() {
    return this.facingMode;
  }

  /**
   * Set callback for camera flip events
   */
  setOnCameraFlip(callback) {
    this.onCameraFlip = callback;
  }

  /**
   * Start screen share capture
   * @param {string} voiceSessionId - The current voice session ID
   * @returns {Promise<MediaStream>} The screen share media stream
   */
  async startScreenShare(voiceSessionId) {
    if (Platform.OS !== 'web') {
      throw new Error('Screen share vision is only supported on web');
    }

    if (this.isCapturing) {
      devLog('[Vision] Already capturing, stopping first');
      await this.stop();
    }

    this.sessionId = voiceSessionId;
    this.source = 'screen';

    try {
      // Request screen share
      devLog('[Vision] Requesting screen share...');
      this.mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          width: { ideal: MAX_FRAME_WIDTH * 2 }, // Higher res for screen
        },
        audio: false,
      });

      // Handle user stopping screen share via browser UI
      const videoTrack = this.mediaStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          devLog('[Vision] Screen share ended by user');
          this.stop();
        };
      }

      // Set up video element for frame capture and WAIT for it to be ready
      await this.setupVideoElement();
      
      // Additional wait for video to start playing
      await this.waitForVideoReady();

      // Start vision session on the backend (REST for realtime, socket otherwise)
      await this._startVisionSession('screen');

      // Start frame capture loop
      this.startFrameCapture();

      devLog('[Vision] Screen share started successfully');
      return this.mediaStream;
    } catch (error) {
      console.error('[Vision] Failed to start screen share:', error);
      await this.cleanup();
      throw error;
    }
  }

  /**
   * Set up video element for frame capture
   */
  setupVideoElement() {
    // Create hidden video element
    this.videoElement = document.createElement('video');
    this.videoElement.srcObject = this.mediaStream;
    this.videoElement.autoplay = true;
    this.videoElement.playsInline = true;
    this.videoElement.muted = true;
    
    // Create canvas for frame extraction
    this.canvasElement = document.createElement('canvas');
    this.canvasContext = this.canvasElement.getContext('2d');
    
    // Wait for video metadata to load
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Video metadata load timeout'));
      }, 10000);

      this.videoElement.onloadedmetadata = () => {
        clearTimeout(timeout);
        devLog('[Vision] Video metadata loaded:', {
          width: this.videoElement.videoWidth,
          height: this.videoElement.videoHeight,
        });
        // Set canvas size based on video dimensions
        const aspectRatio = this.videoElement.videoHeight / this.videoElement.videoWidth;
        this.canvasElement.width = Math.min(this.videoElement.videoWidth, MAX_FRAME_WIDTH);
        this.canvasElement.height = Math.round(this.canvasElement.width * aspectRatio);
        resolve();
      };

      this.videoElement.onerror = (err) => {
        clearTimeout(timeout);
        reject(err);
      };
    });
  }

  /**
   * Wait for video to actually start playing with valid frames
   */
  waitForVideoReady() {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        devWarn('[Vision] Video ready timeout, proceeding anyway');
        resolve();
      }, 5000);

      const checkReady = () => {
        // Check if video has valid dimensions and is playing
        if (this.videoElement && 
            this.videoElement.readyState >= 2 && // HAVE_CURRENT_DATA
            this.videoElement.videoWidth > 0 && 
            this.videoElement.videoHeight > 0) {
          clearTimeout(timeout);
          devLog('[Vision] Video is ready, readyState:', this.videoElement.readyState);
          resolve();
        } else {
          requestAnimationFrame(checkReady);
        }
      };

      // Start playing the video
      this.videoElement.play()
        .then(() => {
          devLog('[Vision] Video play started');
          checkReady();
        })
        .catch((err) => {
          devWarn('[Vision] Video play failed, checking anyway:', err);
          checkReady();
        });
    });
  }

  /**
   * Start the frame capture loop
   */
  startFrameCapture() {
    if (this.isCapturing) return;
    
    this.isCapturing = true;
    this.framesSent = 0;
    this.lastFrameTime = Date.now();

    // Capture and send frames at the configured interval
    this.captureInterval = setInterval(() => {
      this.captureAndSendFrame();
    }, this.frameIntervalMs);

    // Capture first frame immediately
    this.captureAndSendFrame();
    
    devLog('[Vision] Frame capture started at', 1000 / this.frameIntervalMs, 'fps');
  }

  /**
   * Capture current frame and send to backend via WebSocket
   */
  captureAndSendFrame() {
    if (!this.isCapturing || !this.videoElement || !this.canvasContext) {
      return;
    }

    // Backpressure check - skip capture if too many frames are pending
    if (this.pendingFrames >= this.maxPendingFrames) {
      this.droppedFrames++;
      if (this.droppedFrames % 10 === 1) {
        devWarn('[Vision] Dropping frame due to backpressure, pending:', this.pendingFrames, 'dropped total:', this.droppedFrames);
      }
      return;
    }

    try {
      // Draw current video frame to canvas
      this.canvasContext.drawImage(
        this.videoElement,
        0, 0,
        this.canvasElement.width,
        this.canvasElement.height
      );

      // Convert to base64 JPEG
      const frameData = this.canvasElement.toDataURL('image/jpeg', JPEG_QUALITY);
      
      // Extract just the base64 part (remove data:image/jpeg;base64, prefix)
      const base64Data = frameData.split(',')[1];

      // Track pending frame before sending
      this.pendingFrames++;

      if (this.useRestTransport) {
        // Realtime WebRTC: analyze over REST, then inject the resulting
        // context into the realtime conversation via the data channel.
        this._sendFrameViaRest(base64Data);
        return;
      }

      // Send to backend via WebSocket
      const sent = voiceSocketManager.sendVisionFrame(base64Data);
      
      if (sent) {
        this.framesSent++;
        this.lastFrameTime = Date.now();
        devLog('[Vision] Frame sent:', this.framesSent, 'pending:', this.pendingFrames);
      } else {
        // Socket send failed, decrement pending count
        this.pendingFrames = Math.max(0, this.pendingFrames - 1);
      }
      // Note: Frame stats are updated via vision_context event handler in voiceSocket
    } catch (error) {
      console.error('[Vision] Failed to send frame:', error);
      // Don't stop on individual frame failures
    }
  }

  /**
   * Send a frame for analysis over REST (realtime WebRTC sessions) and
   * inject the resulting visual context into the conversation.
   */
  async _sendFrameViaRest(base64Data) {
    const source = this.source;
    try {
      const resp = await voiceApi.sendVisionFrame(this.sessionId, base64Data);
      this.framesSent++;
      this.lastFrameTime = Date.now();

      if (resp.visualContext) {
        const { setVisualContext, incrementVisionFrame } = useVoiceModeStore.getState().actions;
        setVisualContext(resp.visualContext);
        voiceSocketManager.injectVisualContext(resp.visualContext, source);
        // Only count frames that were genuinely analyzed (not rate-limited
        // repeats of the previous context, cached results, or failures).
        if (!resp.skipped && !resp.cached && !resp.analysisError) {
          incrementVisionFrame();
        }
      }
      devLog('[Vision] Frame analyzed via REST:', this.framesSent, resp.skipped ? '(rate limited)' : '');
    } catch (error) {
      devWarn('[Vision] REST frame analysis failed:', error?.message || error);
      // Don't stop on individual frame failures
    } finally {
      this.onFrameProcessed();
    }
  }

  /**
   * Called when a frame has been processed by the backend
   * This should be called from the vision_context event handler
   */
  onFrameProcessed() {
    this.pendingFrames = Math.max(0, this.pendingFrames - 1);
  }

  /**
   * Stop vision capture and cleanup
   */
  async stop() {
    devLog('[Vision] Stopping vision capture');
    
    // Stop capture loop
    this.isCapturing = false;
    if (this.captureInterval) {
      clearInterval(this.captureInterval);
      this.captureInterval = null;
    }

    // Notify backend
    if (this.useRestTransport) {
      const sessionId = this.sessionId;
      if (sessionId) {
        voiceApi.stopVision(sessionId).catch((err) =>
          devWarn('[Vision] Failed to stop vision session:', err?.message || err)
        );
      }
      useVoiceModeStore.getState().actions.stopVision();
    } else {
      voiceSocketManager.stopVision();
      // Note: Store will be updated via vision_stopped event handler in voiceSocket
    }

    // Cleanup resources
    await this.cleanup();
  }

  /**
   * Clean up media resources
   */
  async cleanup() {
    // Stop all media tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Clean up video element
    if (this.videoElement) {
      this.videoElement.srcObject = null;
      this.videoElement = null;
    }

    // Clean up canvas
    this.canvasElement = null;
    this.canvasContext = null;

    // Reset state
    this.sessionId = null;
    this.visionSessionId = null;
    this.source = null;
    this.framesSent = 0;
    this.lastFrameTime = null;
    this.facingMode = 'environment'; // Reset to default
    this.onCameraFlip = null;
    
    // Reset backpressure tracking
    this.pendingFrames = 0;
    this.droppedFrames = 0;

    // Reset transport state
    this.useRestTransport = false;
    this.frameIntervalMs = DEFAULT_FRAME_INTERVAL_MS;
  }

  /**
   * Get current media stream (for video preview)
   */
  getMediaStream() {
    return this.mediaStream;
  }

  /**
   * Check if vision is currently active
   */
  isActive() {
    return this.isCapturing && this.mediaStream !== null;
  }

  /**
   * Get current source type
   */
  getSource() {
    return this.source;
  }

  /**
   * Get stats
   */
  getStats() {
    return {
      framesSent: this.framesSent,
      lastFrameTime: this.lastFrameTime,
      source: this.source,
      isActive: this.isActive(),
      pendingFrames: this.pendingFrames,
      droppedFrames: this.droppedFrames,
    };
  }

}

// Export singleton instance
const voiceVisionService = new VoiceVisionService();
export default voiceVisionService;
